<template>
    <div
        ref="desktopPanel"
        class="desktop-search-panel"
        :class="{ 'is-open': desktopNavStore.getSearchNavState }">
        <section class="search-form">
            <div class="container">
                <div class="row">
                    <div class="col-md-6 offset-md-3">
                        <div class="form-group">
                            <div class="form-control has-icon">
                                <button
                                    type="button"
                                    class="btn-icon"
                                    @click="goToSearch(searchQuery)">
                                    <svgComponent
                                        :width="24"
                                        :height="24"
                                        icon-name="icon-search"></svgComponent>
                                </button>
                                <input
                                    ref="searchInput"
                                    type="text"
                                    id="mobileSearch"
                                    name="mobileSearch"
                                    v-model="searchQuery"
                                    @input="handleInput"
                                    @keyup.esc="handleClose"
                                    @keyup.enter="goToSearch(searchQuery)"
                                    placeholder="Zoeken naar..." />
                            </div>
                            <button
                                class="btn-link btn-link--2"
                                @click="desktopNavStore.toggleSearchNav()">
                                sluiten
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <Transition name="fade-in">
            <template v-if="searchStore.error && !searchStore.loading && searchQuery.length > 3">
                <div class="container">
                    <div class="row">
                        <div class="col-md-6 offset-md-3">
                            <div class="search-highlights">
                                <template v-if="cSearchSuggestions?.length === 0">
                                    <h4>Geen resultaten</h4>
                                    <h5>Probeer een andere zoekterm</h5>
                                </template>
                                <template v-else>
                                    <h4>Er is iets mis gegaan</h4>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template
                v-else-if="
                    cSearchSuggestions != undefined &&
                    !searchStore.loading &&
                    cSearchSuggestions?.length > 0
                ">
                <div class="container">
                    <div class="row">
                        <div class="col-md-6 offset-md-3">
                            <div class="search-highlights">
                                <button
                                    @click="handleSearchSuggestionsClick(item)"
                                    class="item h5"
                                    v-for="(item, index) in cSearchSuggestions"
                                    :key="`suggestion-${index}`"
                                    v-html="useHighlight(item, searchQuery)"></button>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template v-else>
                <section class="popular-panel">
                    <div class="container">
                        <div class="row">
                            <div class="col-4">
                                <popular-grid title="Populaire onderwerpen"></popular-grid>
                            </div>
                            <div class="col-4">
                                <popular-queries
                                    @update="goToSearch"
                                    :items="desktopNavStore.popularKeywords"></popular-queries>
                            </div>
                        </div>
                    </div>
                </section>
            </template>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { useHighlight } from '../../../composables/useHighlight';
import { useDebounceFn, onClickOutside } from '@vueuse/core';
import { ref, computed, watch } from 'vue';
import svgComponent from '../../../components/svgComponent.vue';
import { useDesktopNavStore } from '../store/desktop-nav.store';
import { useSearchStore } from '../../../stores/search.store';
import popularGrid from './popular-grid.component.vue';
import popularQueries from './popular-queries.component.vue';

const desktopNavStore = useDesktopNavStore();
const searchStore = useSearchStore();

const desktopPanel = ref<HTMLDivElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);
const searchQuery = ref<string>('');

const cSearchSuggestions = computed(() => {
    return searchStore.getSearchSuggestions;
});

const cSearchPanelState = computed(() => {
    return desktopNavStore.getSearchNavState;
});

onClickOutside(desktopPanel, () => {
    if (desktopNavStore.getSearchNavState) {
        handleClose();
    }
});

function goToSearch(query: string) {
    searchStore.setSearchQuery(query);
    window.location.href = `/search?query=${searchStore.query}`;
}

function handleSearchSuggestionsClick(item: string) {
    searchStore.postSearchSuggestions(item);
    goToSearch(item);
}

const handleClose = () => {
    desktopNavStore.toggleSearchNav();
    searchStore.clearSearchSuggestions();
    searchQuery.value = '';
};

const handleInput = useDebounceFn(() => {
    if (searchQuery.value.length > 3) {
        searchStore.fetchSearchSuggestions(searchQuery.value);
    } else {
        searchStore.clearSearchSuggestions();
    }
}, 500);

watch(cSearchPanelState, (openState) => {
    if (openState) {
        searchInput.value?.focus();
    } else {
        searchInput.value?.blur();
    }
});
</script>
